import { useEffect, useMemo } from 'react';
import { useGlossary } from '../glossary/GlossaryContext';
import { getTerm, type GlossaryTerm } from '../glossary/terms';
import { Chip } from '../ui';

// ────────────────────────────────────────────────────────────────────────────
// GlossaryPanel — responsive surface that shows a single glossary term.
//
// Layout strategy:
//   - lg+ (≥1024px): right-side gutter, ~360px wide. Slides in from the right.
//     Main content gets a right-side margin so the panel never covers it.
//   - md (768–1023px): right-anchored sheet that overlays content with a
//     dimmable scrim. Tap scrim to close.
//   - sm (<768px): full-width bottom sheet (~70vh) sliding up from below,
//     with a drag-handle affordance.
//
// All three layouts share the same content body, so we render the body once
// and let CSS classes pick the surface chrome.
// ────────────────────────────────────────────────────────────────────────────

export function GlossaryPanel() {
  const { state, close, switchTo, back } = useGlossary();
  const { currentId, history } = state;

  const open = currentId != null;
  const term = currentId ? getTerm(currentId) : undefined;

  // Lock body scroll on mobile/tablet overlay layouts — desktop gutter
  // doesn't overlay so we don't lock there.
  useEffect(() => {
    if (!open) return;
    const isOverlay = window.innerWidth < 1024;
    if (!isOverlay) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Empty render when closed — keeps the DOM clean and screen-reader-quiet.
  if (!open || !term) return null;

  const prevId = history[0] ?? null;
  const prevTerm = prevId ? getTerm(prevId) ?? null : null;

  // Shared body content. Lifted out so each layout shell can wrap it in its
  // own chrome without duplicating the JSX.
  const body = (
    <PanelBody
      term={term}
      prevTerm={prevTerm}
      onBack={back}
      onClose={close}
      onSwitchTo={switchTo}
    />
  );

  return (
    <>
      {/* ─── Backdrop scrim (md / sm only) ─── */}
      <div
        aria-hidden
        onClick={close}
        className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm transition-opacity dark:bg-ink-900/60 lg:hidden"
      />

      {/* ─── Mobile / tablet (sm + md): bottom sheet on sm, right sheet on md ─── */}
      <aside
        role="dialog"
        aria-label={`Glossary: ${term.term}`}
        className="
          fixed inset-x-0 bottom-0 z-50 max-h-[78dvh] overflow-hidden rounded-t-2xl border-t border-ink-200 bg-white shadow-2xl dark:border-ink-700 dark:bg-ink-900
          md:inset-y-0 md:left-auto md:right-0 md:max-h-none md:w-[420px] md:rounded-t-none md:rounded-l-2xl md:border-l md:border-t-0
          lg:hidden
        "
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Drag handle (sm only — md is a side sheet). */}
        <div className="mx-auto mt-2 mb-1 h-1.5 w-10 rounded-full bg-ink-200 dark:bg-ink-700 md:hidden" />
        {body}
      </aside>

      {/* ─── Desktop (lg+): sticky right gutter (no scrim, sits in the
            layout). The Shell adds a paddingRight so this never covers
            content. ─── */}
      <aside
        role="complementary"
        aria-label={`Glossary: ${term.term}`}
        className="
          hidden lg:fixed lg:inset-y-0 lg:right-0 lg:z-30 lg:flex lg:w-[380px] lg:flex-col lg:overflow-hidden lg:border-l lg:border-ink-200 lg:bg-white lg:shadow-xl dark:lg:border-ink-700 dark:lg:bg-ink-900
        "
        style={{
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        {body}
      </aside>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Body — shared between all three layouts.
// ────────────────────────────────────────────────────────────────────────────

interface BodyProps {
  term: GlossaryTerm;
  prevTerm: GlossaryTerm | null;
  onBack: () => void;
  onClose: () => void;
  onSwitchTo: (id: string) => void;
}

function PanelBody({ term, prevTerm, onBack, onClose, onSwitchTo }: BodyProps) {
  // Pre-resolve related entries so we can drop dangling ids gracefully (the
  // tests guarantee no dangling refs ship, but this stays robust during
  // edits).
  const related = useMemo(
    () =>
      (term.related ?? [])
        .map(id => getTerm(id))
        .filter((t): t is GlossaryTerm => Boolean(t)),
    [term.related],
  );

  return (
    <div className="flex h-full max-h-[78dvh] flex-col overflow-hidden md:max-h-none">
      {/* Header */}
      <header className="flex items-start gap-2 border-b border-ink-100 bg-white px-4 py-3 dark:border-ink-700 dark:bg-ink-900">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-accent-600 dark:text-accent-500">
            <span aria-hidden>&#9432;</span>
            <span>Glossary</span>
          </div>
          <h2 className="mt-0.5 text-lg font-bold text-ink-900 dark:text-ink-50">
            {term.term}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close glossary"
          className="rounded-md p-1.5 text-ink-500 transition hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 dark:text-ink-300 dark:hover:bg-ink-700 dark:hover:text-ink-50"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </header>

      {/* Body */}
      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 text-sm">
        {prevTerm && (
          <button
            type="button"
            onClick={onBack}
            className="text-[11px] font-semibold text-accent-700 underline-offset-2 hover:underline dark:text-accent-500"
          >
            ← Back to {prevTerm.term}
          </button>
        )}

        {/* Short definition — slightly larger type */}
        <p className="text-[15px] leading-relaxed text-ink-800 dark:text-ink-100">
          {term.short}
        </p>

        {term.currentValue && (
          <div className="inline-flex items-center gap-2 rounded-lg border border-accent-500/40 bg-accent-50 px-2.5 py-1 text-xs font-medium text-accent-700 dark:border-accent-500/40 dark:bg-accent-700/20 dark:text-accent-500">
            <span aria-hidden>★</span>
            <span>{term.currentValue}</span>
          </div>
        )}

        {/* Long form — split paragraphs on \n\n */}
        <div className="space-y-3 text-[13px] leading-relaxed text-ink-700 dark:text-ink-200">
          {term.long.split('\n\n').map((para, i) => (
            <p key={i} className="whitespace-pre-line">
              {para}
            </p>
          ))}
        </div>

        {/* Learn more (only when a term has an authoritative external reference).
            Cairn-specific concept terms — bucket, scenario, event, etc. — have
            no such reference; the definition itself is the resource. */}
        {term.links.length > 0 && (
        <section>
          <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
            Learn more
          </h3>
          <ul className="space-y-1.5">
            {term.links.map(link => {
              const host = safeHost(link.href);
              return (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-baseline gap-2 rounded-md px-1 py-0.5 text-xs text-accent-700 hover:bg-accent-50 hover:underline dark:text-accent-500 dark:hover:bg-accent-700/10"
                  >
                    <span className="flex-1">{link.label}</span>
                    {host && (
                      <span className="shrink-0 rounded bg-ink-100 px-1.5 py-0.5 text-[10px] font-medium text-ink-500 dark:bg-ink-800 dark:text-ink-400">
                        {host}
                      </span>
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
        </section>
        )}

        {/* Related */}
        {related.length > 0 && (
          <section>
            <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
              Related
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {related.map(r => (
                <Chip
                  key={r.id}
                  size="sm"
                  tone="muted"
                  onClick={() => onSwitchTo(r.id)}
                  title={r.short}
                >
                  {r.term}
                </Chip>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-ink-100 bg-ink-50/60 px-4 py-2.5 text-center dark:border-ink-700 dark:bg-ink-800/40">
        <a
          href="#glossary"
          onClick={onClose}
          className="text-[11px] font-semibold text-accent-700 hover:underline dark:text-accent-500"
        >
          View all terms in glossary →
        </a>
      </footer>
    </div>
  );
}

/** Safe URL host parse — returns null on invalid URLs so layouts don't crash
 *  if a malformed href slips through. */
function safeHost(href: string): string | null {
  try {
    const u = new URL(href);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}
